import {
    AppScreen, Section, SectionHeader, Bleed, Divider,
    Button, ButtonFooter, Badge, TimeBadge,
    Avatar, AvatarStack, AttendanceLegend, Person,
    Chip, InfoGrid, CopyRow, MapBlock, DangerZone,
    CardRail, PlaceCard, InverseBanner, InverseTime,
} from 'components/ds';
import styles from './MeetingPreview.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

/**
 * 모임 상세 미리보기 — /ds/meeting
 *
 * 현재 pages/meeting/MeetingDetail.tsx (3,746줄) 의 실제 섹션 구성을
 * 새 디자인 시스템으로 다시 그린 것입니다. 데이터는 목업입니다.
 *
 * 원본 렌더 순서 (MeetingDetail.tsx 주석 기준, 당일 DAY 모임 · 시작 전):
 *   레이스 벌금 배지 → "내가 쏜다" 배너 → 임박 알림 배너 → 지도(+상태 배지)
 *   → 장소 칩 목록 → 정보 그리드(날짜|시간, 주최자|카테고리) → 초대코드
 *   → 참여자 → 장소 & 정산 → 기타 지출 → 시간 연장/모임 종료 → 삭제·나가기
 *
 * 생략한 것 (조건부라 이 케이스에 안 나옴 — 지어내지 않았습니다)
 *   · 여행 일정(travelSchedule) · 숙소(accommodationTitle) — 여행 모임 전용
 *   · 지난 모임 정산 요약 Hero · 재오픈 — 종료된 모임 전용
 *   · 일정 미정 조율 Hero — 일정 미확정일 때만
 */

const LABELS = { going: '참여', pending: '미정', declined: '불참' };

const MEMBERS: Person[] = [
    { userNo: 1, name: '재권', attendance: 'going', isHost: true },
    { userNo: 2, name: '민서', attendance: 'going' },
    { userNo: 3, name: '태윤', attendance: 'going' },
    { userNo: 4, name: '지훈', attendance: 'pending' },
    { userNo: 5, name: '하늘', attendance: 'pending' },
    { userNo: 6, name: '수아', attendance: 'declined' },
];

const IMG = {
    bar: 'https://images.unsplash.com/photo-1470337458703-46ad1756a187?w=640&h=400&fit=crop',
    cafe: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=400&h=400&fit=crop',
    dessert: 'https://images.unsplash.com/photo-1563805042-7684c019e1cb?w=400&h=400&fit=crop',
    wine: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400&h=400&fit=crop',
};

const IcShare = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
        <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
    </svg>
);

export const MeetingPreview = () => (
    <AppScreen
        title="금요일 저녁 번개"
        bar="sub"
        hasFooter
        right={
            <button type="button" className={cls("mp-icon-btn")} aria-label="공유">
                <IcShare />
            </button>
        }
    >
        {/* ── 임박 알림 배너 — 1시간 이내 시작/진행 중 ── */}
        <Section spacing="tight">
            <Bleed>
                <InverseBanner
                    title="곧 시작해요"
                    sub={<><InverseTime>2시간 후</InverseTime> · 아직 3명이 응답하지 않았어요</>}
                />
            </Bleed>
        </Section>

        {/* ── 지도 + 상태 배지 ── */}
        <Section spacing="tight">
            <Bleed>
                <MapBlock
                    badge={<Badge tone="brand">모집 중</Badge>}
                    placeName="소금집 델리"
                    ariaLabel="지도에서 보기"
                    actions={
                        <span className={cls("mp-map-actions")}>
                            <span className={cls("mp-map-btn")}>카카오맵</span>
                            <span className={cls("mp-map-btn")}>길찾기</span>
                        </span>
                    }
                >
                    <img className={cls("mp-map-img")} src={IMG.bar} alt="" />
                </MapBlock>
            </Bleed>
        </Section>

        {/* ── 장소 목록 — 선택 가능한 칩 ── */}
        <Section spacing="tight">
            <SectionHeader title="장소" count={3} moreLabel="장소 추가" onMore={() => undefined} />
            <div className={cls("mp-chips")}>
                <Chip label="1차 · 소금집 델리" selected onSelect={() => undefined} />
                <Chip label="2차 · 연남 브루어리" onSelect={() => undefined} />
                <Chip label="3차 · 카페 미아" onSelect={() => undefined} />
            </div>
        </Section>

        {/* ── 정보 그리드 ── */}
        <Section spacing="tight">
            <InfoGrid
                items={[
                    { label: '날짜', value: '9월 5일 (금)' },
                    { label: '시간', value: '오후 7시' },
                    { label: '주최자', value: '재권' },
                    { label: '카테고리', value: '술 · 회식' },
                ]}
            />
        </Section>

        {/* ── 초대 코드 ── */}
        <Section spacing="tight">
            <CopyRow
                label="초대 코드"
                value="CREW-4K7QX2"
                copyLabel="복사"
                onCopy={() => undefined}
            />
        </Section>

        {/* ── 참여자 ── */}
        <Section divided>
            <SectionHeader
                title="참여자"
                count={6}
                sub="3명 참여 · 2명 미정 · 1명 불참"
                moreLabel="초대하기"
                onMore={() => undefined}
            />
            <div className={cls("mp-members")}>
                {MEMBERS.map((m) => (
                    <span key={m.userNo} className={cls("mp-member")}>
                        <Avatar person={m} size="lg" statusLabel={LABELS[m.attendance || 'pending']} />
                        <span className={cls("mp-member-name")}>{m.name}</span>
                    </span>
                ))}
            </div>
            <AttendanceLegend labels={LABELS} />

            <Divider />

            <div className={cls("mp-my-state")}>
                <span className={cls("mp-my-state-label")}>내 응답</span>
                <div className={cls("mp-chips")}>
                    <Chip label="참여" selected onSelect={() => undefined} />
                    <Chip label="미정" onSelect={() => undefined} />
                    <Chip label="불참" onSelect={() => undefined} />
                </div>
            </div>
        </Section>

        {/* ── 장소 & 정산 ── */}
        <Section divided>
            <SectionHeader
                title="장소 & 정산"
                sub="장소별로 쓴 돈을 기록하면 자동으로 나눠집니다"
                moreLabel="정산하기"
                onMore={() => undefined}
            />
            <div className={cls("mp-settle")}>
                <div className={cls("mp-settle-row")}>
                    <span className={cls("mp-settle-place")}>1차 · 소금집 델리</span>
                    <span className={cls("mp-settle-amt")}>128,000원</span>
                </div>
                <div className={cls("mp-settle-row")}>
                    <span className={cls("mp-settle-place")}>2차 · 연남 브루어리</span>
                    <span className={cls("mp-settle-amt mp-settle-none")}>미입력</span>
                </div>
                <div className={cls("mp-settle-row")}>
                    <span className={cls("mp-settle-place")}>기타 지출</span>
                    <span className={cls("mp-settle-amt")}>18,500원</span>
                </div>
            </div>
            <InfoGrid
                columns={1}
                items={[{ label: '내 몫', value: '48,833원 · 재권님에게 보내기' }]}
            />
        </Section>

        {/* ── 이 근처 다음 장소 추천 ── */}
        <Section divided>
            <SectionHeader title="이 근처 다음 장소" sub="소금집 델리에서 걸어갈 수 있어요" />
            <CardRail>
                <PlaceCard name="연남 브루어리" meta="도보 4분" imageUrl={IMG.wine} />
                <PlaceCard name="카페 미아" meta="도보 6분" imageUrl={IMG.cafe} />
                <PlaceCard name="설빙 홍대점" meta="도보 8분" imageUrl={IMG.dessert} />
            </CardRail>
        </Section>

        {/* ── 위험 영역 ── */}
        <Section divided last>
            <DangerZone title="이 모임에서">
                <Button variant="secondary" size="sm">모임 종료하기</Button>
                <Button variant="danger" size="sm">모임 나가기</Button>
                <Button variant="danger" size="sm">모임 삭제</Button>
            </DangerZone>
        </Section>

        <ButtonFooter>
            <Button variant="secondary">채팅</Button>
            <Button variant="brand" block>출발 알리기</Button>
        </ButtonFooter>
    </AppScreen>
);
